import { useState, useMemo } from 'react'
import axios from 'axios'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'

import { API_BASE_URL } from '../constants/system'
import useAccount from './useAccount'
import { extractErrorMessage } from '../utils/apitools'

import {
  MultiTableConfigTable,
  MultiTable,
} from '../types/gotypes'

export interface UseDashboardMultiTableProps {
  customer: string
  dashboardId: string
  multiTableId?: string // Optional - if provided, load this specific multi-table
}

/**
 * Hook for managing multi-tables owned by a dashboard.
 * Dashboard multi-tables don't appear in the user's TOC.
 * Dashboard multi-tables can contain tables from multiple jobs.
 */
export const useDashboardMultiTable = ({
  customer,
  dashboardId,
  multiTableId,
}: UseDashboardMultiTableProps) => {
  const account = useAccount()
  const queryClient = useQueryClient()
  const [tables, setTables] = useState<MultiTableConfigTable[]>([])

  const multiTablesQueryKey = useMemo(() => {
    return ['dashboardMultiTables', customer, dashboardId]
  }, [customer, dashboardId])

  const singleMultiTableQueryKey = useMemo(() => {
    return ['dashboardMultiTable', customer, dashboardId, multiTableId]
  }, [customer, dashboardId, multiTableId])

  // List all multi-tables for this dashboard
  const listQuery = useQuery({
    queryKey: multiTablesQueryKey,
    enabled: Boolean(customer && dashboardId && account.user),
    queryFn: async () => {
      const response = await axios.get<{ multitables: MultiTable[] }>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/multitables`
      )
      return response.data.multitables || []
    },
  })

  // Get a specific multi-table
  const getQuery = useQuery({
    queryKey: singleMultiTableQueryKey,
    enabled: Boolean(customer && dashboardId && multiTableId && account.user),
    queryFn: async () => {
      const response = await axios.get<MultiTable>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/multitables/${multiTableId}`
      )
      return response.data
    },
  })

  // Create a new multi-table for this dashboard
  const createMutation = useMutation({
    mutationFn: async (config: { tables: MultiTableConfigTable[], name?: string, persona?: string }) => {
      const response = await axios.post<MultiTable>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/multitables`,
        {
          config: {
            name: config.name || 'Dashboard Analysis',
            tables: config.tables,
            description: '',
            global_filter: '',
          },
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: multiTablesQueryKey })
    },
  })

  // Update an existing multi-table
  const updateMutation = useMutation({
    mutationFn: async ({ id, config }: { id: string, config: { tables: MultiTableConfigTable[], name?: string } }) => {
      const response = await axios.put<MultiTable>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/multitables/${id}`,
        {
          config: {
            name: config.name || 'Dashboard Analysis',
            tables: config.tables,
            description: '',
            global_filter: '',
          },
        }
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: multiTablesQueryKey })
      // Invalidate by the MUTATED id, not this instance's `multiTableId`. The
      // write-path instances (DashboardEditor, GenAIWizard, useDashboardCellSave)
      // are constructed without a multiTableId, so the instance key ends in
      // `undefined` and matches no cached query — the widget's id-keyed copy
      // kept the pre-change tables, and its "Settings changed — data not yet
      // regenerated" banner stuck until a full browser refresh (#1133).
      queryClient.invalidateQueries({ queryKey: ['dashboardMultiTable', customer, dashboardId, variables.id] })
    },
  })

  // Delete a multi-table
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/multitables/${id}`
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: multiTablesQueryKey })
    },
  })

  const multiTable = getQuery.data
  const multitables = listQuery.data || []

  const isInMultiTable = (tablePath: string) => {
    return tables.some(table => {
      const fullPath = table.folderPath ? `${table.folderPath}/${table.filename}` : table.filename
      return fullPath === tablePath
    })
  }

  const addTable = (table: MultiTableConfigTable) => {
    const fullPath = table.folderPath ? `${table.folderPath}/${table.filename}` : table.filename
    if (!isInMultiTable(fullPath)) {
      setTables([...tables, table])
    }
  }

  const removeTable = (index: number) => {
    setTables(tables.filter((_, i) => i !== index))
  }

  const clearTables = () => {
    setTables([])
  }

  // Initialize tables from loaded multi-table
  const initializeTables = (loadedTables: MultiTableConfigTable[]) => {
    setTables(loadedTables)
  }

  return {
    // Data
    multitables,
    multiTable,
    tables,

    // Table management
    setTables,
    addTable,
    removeTable,
    clearTables,
    isInMultiTable,
    initializeTables,

    // Mutations
    createMultiTable: createMutation.mutateAsync,
    updateMultiTable: updateMutation.mutateAsync,
    deleteMultiTable: deleteMutation.mutateAsync,

    // Loading states
    isLoading: listQuery.isLoading || getQuery.isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Errors
    error: listQuery.error || getQuery.error || createMutation.error || updateMutation.error || deleteMutation.error,
    errorMessage: extractErrorMessage(listQuery.error || getQuery.error || createMutation.error || updateMutation.error || deleteMutation.error),

    // Refresh
    refresh: () => {
      queryClient.invalidateQueries({ queryKey: multiTablesQueryKey })
      if (multiTableId) {
        queryClient.invalidateQueries({ queryKey: singleMultiTableQueryKey })
      }
    },
  }
}

export default useDashboardMultiTable
